/**
 * Compact settings for AI usage soft/hard limits, reserve %, and warn thresholds.
 *
 * Writes through set_ai_usage_policy (owner aal2). Does not interrupt work —
 * policy changes are advisory inputs for meters and Overseer copy.
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { checkAiUsagePolicy, policyFromRow, type AiUsagePolicyRow } from '../lib/ai-usage-view';
import { errorMessage } from '../lib/mutations';

export interface AiUsagePolicySettingsProps {
  readonly client: SupabaseClient;
  readonly orgId: string;
  readonly policy: AiUsagePolicyRow | null;
  readonly onSaved: () => void;
}

export function AiUsagePolicySettings({
  client,
  orgId,
  policy,
  onSaved,
}: AiUsagePolicySettingsProps): ReactElement {
  const current = policyFromRow(policy);
  const [soft, setSoft] = useState(current.softLimitPercent === null ? '' : String(current.softLimitPercent));
  const [hard, setHard] = useState(current.hardLimitPercent === null ? '' : String(current.hardLimitPercent));
  const [reserve, setReserve] = useState(current.reservePercent === null ? '' : String(current.reservePercent));
  const [warns, setWarns] = useState(current.warnThresholds.join(', '));
  const [behavior, setBehavior] = useState(current.exhaustionBehavior);
  const [neverSub, setNeverSub] = useState(current.neverSubstitutePinned);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setOk(null);
    const checked = checkAiUsagePolicy({
      softLimitPercent: soft,
      hardLimitPercent: hard,
      reservePercent: reserve,
      warnThresholds: warns,
      exhaustionBehavior: behavior,
      neverSubstitutePinned: neverSub,
    });
    if (!checked.ok) {
      setError(checked.message);
      return;
    }
    setBusy(true);
    try {
      const request = {
        orgId,
        softLimitPercent: checked.value.softLimitPercent,
        hardLimitPercent: checked.value.hardLimitPercent,
        reservePercent: checked.value.reservePercent,
        warnThresholds: [...checked.value.warnThresholds],
        exhaustionBehavior: checked.value.exhaustionBehavior,
        neverSubstitutePinned: checked.value.neverSubstitutePinned,
      };
      const { data, error: transportError } = await client.rpc('set_ai_usage_policy', { request });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return;
      }
      setOk('Usage policy saved. Overseer will advise from these limits.');
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack ai-usage-policy" onSubmit={(event) => { void onSubmit(event); }}>
      <p className="muted">
        Soft and hard limits are percent of a known allowance. Leave a box empty to leave that limit unset.
        Explicit model pins are never silently substituted.
      </p>
      <div className="row wrap ai-usage-policy-fields">
        <label>
          Soft limit %
          <input aria-label="Soft usage limit percent" inputMode="numeric" maxLength={3} value={soft}
            onChange={(event) => setSoft(event.target.value)} placeholder="e.g. 75" />
        </label>
        <label>
          Hard limit %
          <input aria-label="Hard usage limit percent" inputMode="numeric" maxLength={3} value={hard}
            onChange={(event) => setHard(event.target.value)} placeholder="e.g. 95" />
        </label>
        <label>
          Reserve %
          <input aria-label="Reserve percent for high-priority work" inputMode="numeric" maxLength={2} value={reserve}
            onChange={(event) => setReserve(event.target.value)} placeholder="0–90" />
        </label>
        <label className="ai-usage-policy-warns">
          Warn at %
          <input aria-label="Warn thresholds percent" value={warns}
            onChange={(event) => setWarns(event.target.value)} placeholder="50, 75, 90, 100" />
        </label>
      </div>
      <label>
        When exhausted
        <select aria-label="Exhaustion behavior" value={behavior}
          onChange={(event) => setBehavior(event.target.value as typeof current.exhaustionBehavior)}>
          <option value="advise_only">Advise only</option>
          <option value="auto_route">Auto-route by policy</option>
          <option value="pause_lane">Pause only that lane</option>
          <option value="require_approval">Require approval</option>
        </select>
      </label>
      <label className="row">
        <input type="checkbox" checked={neverSub}
          onChange={(event) => setNeverSub(event.target.checked)} />
        Never silently substitute an explicitly pinned offering
      </label>
      <div className="row">
        <button type="submit" className="primary" disabled={busy}>Save usage policy</button>
        {ok !== null && <small className="muted">{ok}</small>}
      </div>
      {error !== null && <p role="alert">{error}</p>}
    </form>
  );
}
